import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  eventImage,
  formatDateParts,
  getHeaderImage,
  getBtvFallbackForBg,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Drab = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const accent = resolveColor(themeColors.accentColor, customizations?.accentColor);
  const secondaryBg = resolveColor(themeColors.secondaryBackgroundColor, customizations?.secondaryBackgroundColor);
  const heading = getRelativeContrastColor(
    resolveColor(themeColors.headingTextColor, customizations?.headingTextColor),
    container
  );
  const headingOnSecondaryBg = getRelativeContrastColor(
    resolveColor(themeColors.headingTextColor, customizations?.headingTextColor),
    secondaryBg
  );
  const body = getRelativeContrastColor(
    resolveColor(themeColors.bodyTextColor, customizations?.bodyTextColor),
    container
  );
  const buttonText = getRelativeContrastColor(
    resolveColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor, customizations?.buttonTextColor),
    accent
  );
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionParts = event.sessions[0]?.timestamp ? formatDateParts(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(container);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Impact, sans-serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Arial, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || 'You have been invited to:');
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Drab"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--secondary-bg': secondaryBg,
          '--body': body,
          '--heading': heading,
          '--heading-on-secondary-bg': headingOnSecondaryBg,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td align="center" valign="middle" height={48} style={{ padding: '8px' }}>
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '0 32px' }}>
                    <div style={{ borderTop: '1px solid var(--heading)', fontSize: 0, lineHeight: '0' }}>&nbsp;</div>
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ padding: '24px 16px 8px 16px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '20px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 400,
                        textTransform: 'uppercase',
                        lineHeight: '20px',
                        letterSpacing: '0.6px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ padding: '0 16px 24px 16px' }}>
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '56px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        lineHeight: '56px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td align="center" style={{ padding: '24px' }}>
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={324} src={bodyImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>
                {eventDescription && (
                  <tr>
                    <td align="center" style={{ padding: '0 40px 32px 40px' }}>
                      <ContentEditable
                        value={eventDescription}
                        setValue={setDescription}
                        style={{
                          color: 'var(--heading)',
                          fontSize: '16px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 400,
                          lineHeight: '19.2px',
                          wordWrap: 'break-word',
                          textAlign: 'center',
                          maxWidth: '317px',
                        }}
                      />
                    </td>
                  </tr>
                )}
                {sessionParts && (
                  <tr>
                    <td align="center" style={{ padding: '0 24px 32px 24px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td
                              align="center"
                              style={{
                                background: 'var(--secondary-bg)',
                                border: '1px solid var(--heading-on-secondary-bg)',
                                padding: '24px',
                                borderRadius: 25,
                                filter: 'brightness(1.6) saturate(0.5)',
                              }}
                            >
                              <div style={{ textAlign: 'center', width: '98px' }}>
                                <div
                                  style={{
                                    color: 'var(--heading-on-secondary-bg)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    lineHeight: '18px',
                                    letterSpacing: '0.48px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.dayName}
                                </div>
                                <div
                                  style={{
                                    color: 'var(--heading-on-secondary-bg)',
                                    fontSize: '32px',
                                    fontFamily: 'var(--heading-font)',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    lineHeight: '32px',
                                    letterSpacing: '0.96px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.numericDate}
                                </div>
                              </div>
                            </td>
                            <td width={20}>&nbsp;</td>
                            <td
                              align="center"
                              style={{
                                background: 'var(--secondary-bg)',
                                border: '1px solid var(--heading-on-secondary-bg)',
                                padding: '24px',
                                borderRadius: 25,
                                filter: 'brightness(1.6) saturate(0.5)',
                              }}
                            >
                              <div style={{ textAlign: 'center' }}>
                                <div
                                  style={{
                                    color: 'var(--heading-on-secondary-bg)',
                                    fontSize: '14px',
                                    fontFamily: 'var(--body-font)',
                                    fontWeight: 500,
                                    textTransform: 'uppercase',
                                    lineHeight: '18px',
                                    letterSpacing: '0.48px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.period}
                                </div>
                                <div
                                  style={{
                                    color: 'var(--heading-on-secondary-bg)',
                                    fontSize: '32px',
                                    fontFamily: 'var(--heading-font)',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    lineHeight: '32px',
                                    wordWrap: 'break-word',
                                  }}
                                >
                                  {sessionParts.time}
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                <tr>
                  <td align="center" style={{ paddingBottom: '32px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{ background: 'var(--accent)', width: '148px', height: '40px' }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                textDecoration: 'none',
                                padding: '6px 0 6px 0',
                                width: '148px',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '13px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  lineHeight: '16px',
                                  letterSpacing: '0.39px',
                                  wordWrap: 'break-word',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--body)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--body)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
